import { useEffect, useState } from "react";

/**
 * CharityUkAdminPanel — Panneau de suivi de l'enregistrement Charity Commission (UK).
 * Timeline visuelle + formulaire de statut + prochaines étapes.
 */
const STATUS_STEPS = [
  { id: "preparation", label: "Préparation du dossier", icon: "📚" },
  { id: "soumis",      label: "Dossier soumis",          icon: "📮" },
  { id: "en_cours",    label: "En cours d'examen",       icon: "🔍" },
  { id: "approuve",    label: "Approuvé — enregistré",   icon: "✅" },
  { id: "rejete",      label: "Rejeté / à corriger",     icon: "⚠️" },
];

const CHECKLIST = [
  { id: "trustees_declaration",   label: "Trustees' Declaration signée par tous les administrateurs" },
  { id: "constitution",           label: "Constitution CIO validée (modèle Charity Commission)" },
  { id: "financial_confidence",   label: "Financial Confidence Statement + projections 3 ans" },
  { id: "trustees_ids",           label: "Copies pièces d'identité des Trustees (Passeport)" },
  { id: "safeguarding",           label: "Safeguarding Policy (Protection des enfants)" },
  { id: "reserves_policy",        label: "Reserves Policy (politique de réserves)" },
  { id: "conflict_interest",      label: "Politique de conflits d'intérêts adoptée" },
  { id: "bank_account",           label: "Compte bancaire UK ouvert (Revolut Business)" },
  { id: "annual_return_ready",    label: "Modèle d'Annual Return préparé pour le 1er exercice" },
];

export const CharityUkAdminPanel = ({ API, pinInput }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [checked, setChecked] = useState({});

  const pin = encodeURIComponent(pinInput || "SAFUAYE2024");

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/settings?pin=${pin}`);
      const d = await r.json();
      setSettings(d);
      setChecked(d?.charity_uk?.checklist || {});
    } catch (e) { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/admin/settings?pin=${pin}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ charity_uk: { ...settings?.charity_uk, ...patch } }),
      });
      const d = await r.json();
      setSettings(d.settings);
    } catch (e) { /* ignore */ }
    setSaving(false);
  };

  const toggleCheck = (id) => {
    const next = { ...checked, [id]: !checked[id] };
    setChecked(next);
    save({ checklist: next });
  };

  if (loading || !settings) return <div className="p-6 text-sm text-gray-500">Chargement…</div>;

  const c = settings.charity_uk || {};
  const currentStepIndex = STATUS_STEPS.findIndex(s => s.id === c.status);
  const doneCount = Object.values(checked).filter(Boolean).length;

  return (
    <div data-testid="admin-charity-uk-panel" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-heading text-xl font-bold text-gray-900 flex items-center gap-2">
          🇬🇧 Charity Commission (UK) — Suivi d'enregistrement
        </h3>
        <button
          onClick={load}
          className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-800 px-3 py-2 rounded font-semibold"
          data-testid="admin-charity-refresh"
        >
          🔄 Rafraîchir
        </button>
      </div>

      {/* Timeline */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-bold text-gray-800 mb-3">Timeline</h4>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {STATUS_STEPS.filter(s => s.id !== "rejete").map((s, i) => {
            const active = i <= currentStepIndex && c.status !== "rejete";
            return (
              <button
                key={s.id}
                onClick={() => save({ status: s.id })}
                data-testid={`admin-charity-status-${s.id}`}
                className={`p-3 rounded-lg text-center border-2 transition-all ${
                  active
                    ? "bg-emerald-600 text-white border-emerald-700 shadow"
                    : "bg-gray-50 text-gray-500 border-gray-200 hover:border-emerald-300"
                }`}
              >
                <div className="text-xl">{s.icon}</div>
                <div className="text-[11px] font-semibold mt-1">{s.label}</div>
              </button>
            );
          })}
        </div>
        {c.status === "rejete" && (
          <div className="mt-3 bg-red-50 border-l-4 border-red-600 p-3 text-xs text-red-800 rounded">
            ⚠️ Dossier rejeté — voir les notes ci-dessous pour la correction demandée.
          </div>
        )}
        <button
          onClick={() => save({ status: "rejete" })}
          className="mt-2 text-[11px] text-red-600 hover:underline"
          data-testid="admin-charity-status-rejete"
        >
          Marquer comme rejeté / à corriger
        </button>
      </div>

      {/* Coordonnées enregistrement */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 grid md:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs font-semibold text-gray-700">Type d'entité</span>
          <input
            type="text"
            value={c.cio_type || ""}
            onChange={e => setSettings({ ...settings, charity_uk: { ...c, cio_type: e.target.value } })}
            onBlur={() => save({ cio_type: c.cio_type })}
            className="mt-1 w-full text-sm border border-gray-300 rounded px-2 py-1.5"
            data-testid="admin-charity-cio-type"
          />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-gray-700">Charity Number (dès attribution)</span>
          <input
            type="text"
            placeholder="ex : 1234567"
            value={c.charity_number || ""}
            onChange={e => setSettings({ ...settings, charity_uk: { ...c, charity_number: e.target.value } })}
            onBlur={() => save({ charity_number: c.charity_number })}
            className="mt-1 w-full text-sm border border-gray-300 rounded px-2 py-1.5"
            data-testid="admin-charity-number"
          />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-gray-700">Date de soumission</span>
          <input
            type="date"
            value={c.date_submission || ""}
            onChange={e => setSettings({ ...settings, charity_uk: { ...c, date_submission: e.target.value } })}
            onBlur={() => save({ date_submission: c.date_submission })}
            className="mt-1 w-full text-sm border border-gray-300 rounded px-2 py-1.5"
            data-testid="admin-charity-date-submission"
          />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-gray-700">Date d'approbation</span>
          <input
            type="date"
            value={c.date_approval || ""}
            onChange={e => setSettings({ ...settings, charity_uk: { ...c, date_approval: e.target.value } })}
            onBlur={() => save({ date_approval: c.date_approval })}
            className="mt-1 w-full text-sm border border-gray-300 rounded px-2 py-1.5"
            data-testid="admin-charity-date-approval"
          />
        </label>
        <label className="block md:col-span-2">
          <span className="text-xs font-semibold text-gray-700">Référence dossier (ID Charity Commission)</span>
          <input
            type="text"
            placeholder="ex : CC-12345-ABCD"
            value={c.reference_case || ""}
            onChange={e => setSettings({ ...settings, charity_uk: { ...c, reference_case: e.target.value } })}
            onBlur={() => save({ reference_case: c.reference_case })}
            className="mt-1 w-full text-sm border border-gray-300 rounded px-2 py-1.5"
            data-testid="admin-charity-reference"
          />
        </label>
        <label className="block md:col-span-2">
          <span className="text-xs font-semibold text-gray-700">Prochaine étape</span>
          <input
            type="text"
            value={c.next_step || ""}
            onChange={e => setSettings({ ...settings, charity_uk: { ...c, next_step: e.target.value } })}
            onBlur={() => save({ next_step: c.next_step })}
            className="mt-1 w-full text-sm border border-gray-300 rounded px-2 py-1.5"
            data-testid="admin-charity-next-step"
          />
        </label>
        <label className="block md:col-span-2">
          <span className="text-xs font-semibold text-gray-700">Notes internes</span>
          <textarea
            rows={3}
            value={c.notes || ""}
            onChange={e => setSettings({ ...settings, charity_uk: { ...c, notes: e.target.value } })}
            onBlur={() => save({ notes: c.notes })}
            className="mt-1 w-full text-sm border border-gray-300 rounded px-2 py-1.5"
            data-testid="admin-charity-notes"
          />
        </label>
      </div>

      {/* Checklist */}
      <div className="bg-white border border-amber-300 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-bold text-amber-900">✅ Checklist Charity Commission ({doneCount}/{CHECKLIST.length})</h4>
        </div>
        <ul className="space-y-1 text-sm">
          {CHECKLIST.map(item => (
            <li key={item.id} className="flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-1 accent-emerald-600"
                checked={!!checked[item.id]}
                onChange={() => toggleCheck(item.id)}
                data-testid={`admin-charity-check-${item.id}`}
              />
              <span className={checked[item.id] ? "line-through text-gray-500" : "text-gray-800"}>{item.label}</span>
            </li>
          ))}
        </ul>
      </div>

      {saving && <div className="text-xs text-emerald-700">💾 Enregistrement…</div>}
    </div>
  );
};

export default CharityUkAdminPanel;
